/*
 * getErrorListData  functions
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ERROR_LIST_ITEM_DATA_TYPE,
	IErrorListGroup,
	IErrorListItemCategoryData,
	IErrorListItemErrorData,
	OnErrorItemClick
} from "./ErrorListTypes";
import {
	DOC_ERROR_SEVERITY,
	ICompileError,
	IDocumentError,
	IRuntimeError
} from "@adapptio/lib-blueprint";
import { IErrorListItemProps } from "./ErrorListItem";
import { isFunction } from "@adapptio/lib-shared";

const createCategoryTitleData = (value: IErrorListItemCategoryData["value"]): IErrorListItemCategoryData => ({
	type: ERROR_LIST_ITEM_DATA_TYPE.CATEGORY_TITLE,
	value: {
		...value
	}
});

const createErrorData = (value: IErrorListItemErrorData["value"]): IErrorListItemErrorData => ({
	type: ERROR_LIST_ITEM_DATA_TYPE.ERROR,
	value: {
		...value
	}
});

type CommonError = IRuntimeError | ICompileError;
type ErrorCountById = Record<string, number>;

const mergeErrors = <T extends CommonError>(
	errors: T[],
	{ severityFilter }: { severityFilter?: Set<DOC_ERROR_SEVERITY> }
) =>
	errors.reduce<{ mergedErrors: T[]; errorCountById: ErrorCountById }>(
		(acc, error) => {
			if (severityFilter && severityFilter?.size > 0 && !severityFilter?.has(error?.severity)) {
				return acc;
			}

			return {
				...acc,
				mergedErrors: [
					...acc.mergedErrors,
					...(acc.errorCountById[error.modelNodeId] > 0 ? [] : [ error ])
				],
				errorCountById: {
					...acc.errorCountById,
					[error.modelNodeId]: (acc.errorCountById[error.modelNodeId] || 0) + 1
				}
			};
		},
		{
			errorCountById: {},
			mergedErrors: []
		}
	);

const createErrorItemData = <T extends CommonError>(
	errors: T[],
	errorCountById: ErrorCountById,
	{ onClick, inset }: { onClick: IErrorListItemProps["onClick"]; inset: boolean }
) =>
	errors.reduce<IErrorListItemErrorData[]>(
		(acc, error) => [
			...acc,
			createErrorData({
				error,
				onClick,
				count: errorCountById[error.modelNodeId],
				inset
			})
		],
		[]
	);

const createCommonErrorsItems = <T extends CommonError>(
	errors: T[],
	{
		inset,
		severityFilter,
		onClick
	}: {
		inset?: boolean;
		severityFilter?: Set<DOC_ERROR_SEVERITY>;
		onClick?: IErrorListItemProps["onClick"];
	}
) => {
	const result = [];

	if (!(errors?.length > 0)) {
		return result;
	}

	const { mergedErrors, errorCountById } = mergeErrors(errors, {
		severityFilter
	});

	if (!(mergedErrors.length > 0)) {
		return result;
	}

	const commonErrorsData = createErrorItemData(mergedErrors, errorCountById, {
		onClick,
		inset
	});

	result.push(...commonErrorsData);

	return result;
};

export interface IGetErrorsDataParams {
	severityFilter?: Set<DOC_ERROR_SEVERITY>;
	insertCategoryTitles?: boolean;
	onCompileErrorClick?: OnErrorItemClick<ICompileError>;
	onParseErrorClick?: OnErrorItemClick<IDocumentError>;
	onRuntimeErrorClick?: OnErrorItemClick<IRuntimeError>;
}

export const getErrorsData = (
	errorGroups: IErrorListGroup[],
	{
		severityFilter,
		insertCategoryTitles,
		onCompileErrorClick,
		onParseErrorClick,
		onRuntimeErrorClick
	}: IGetErrorsDataParams
) => {
	const allData = [];

	for (let errorGroupIndex = 0; errorGroupIndex < errorGroups.length; errorGroupIndex++) {
		const errorGroup = errorGroups[errorGroupIndex];
		const { documentUri, parseErrors, runtimeErrors, compileErrors } = errorGroup;
		const inset = !!documentUri;

		if (documentUri) {
			allData.push(createCategoryTitleData({ children: documentUri }));
		}

		const getOnErrorClick =
			<ErrorType extends ICompileError | IDocumentError | IRuntimeError>(
				onErrorClick: OnErrorItemClick<ErrorType>
			) =>
			(error: ErrorType) => {
				if (isFunction(onErrorClick)) {
					onErrorClick(error, errorGroup);
				}
			};

		if (parseErrors?.length > 0) {
			const parseErrorsData = parseErrors.reduce((acc, error) => {
				if (severityFilter && severityFilter?.size > 0 && !severityFilter?.has(error.severity)) {
					return acc;
				}

				return [
					...acc,
					createErrorData({
						error,
						onClick: getOnErrorClick<IDocumentError>(onParseErrorClick),
						inset
					})
				];
			}, []);

			if (insertCategoryTitles && parseErrorsData.length > 0) {
				allData.push(createCategoryTitleData({ children: "Parse errors", secondary: true, inset }));
			}

			allData.push(...parseErrorsData);
		}

		if (runtimeErrors?.length > 0) {
			const runtimeErrorsData = createCommonErrorsItems(runtimeErrors, {
				onClick: getOnErrorClick<IRuntimeError>(onRuntimeErrorClick),
				inset,
				severityFilter
			});

			if (insertCategoryTitles && runtimeErrorsData?.length > 0) {
				allData.push(createCategoryTitleData({ children: "Runtime errors", secondary: true, inset }));
			}

			allData.push(...runtimeErrorsData);
		}

		if (compileErrors?.length > 0) {
			const compileErrorsData = createCommonErrorsItems(compileErrors, {
				onClick: getOnErrorClick<ICompileError>(onCompileErrorClick),
				inset,
				severityFilter
			});

			if (insertCategoryTitles && compileErrorsData?.length > 0) {
				allData.push(createCategoryTitleData({ children: "Compile errors", secondary: true, inset }));
			}

			allData.push(...compileErrorsData);
		}
	}

	return allData;
};
